import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../routes";
import "./terms.scss";
import useScreenSize from "../../hooks/useScreenSize";

export default function TermsConditions() {
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const [showScrollTop, setShowScrollTop] = useState(false);

  const handleLogin = () => {
    navigate(ROUTES.PATHS.LOGIN);
  };

  const handleScrollTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (<div className="terms-page">
    <header>
      <div className="container">
        <nav>
          <a className="logo" href="/">
            <span className="logo-dot"></span>
            <strong>SerialMan</strong>
            {!isMobile && <span style={{ opacity: 0.6, fontSize: "0.9rem" }}>Queue & Appointments</span>}
          </a>
          <div className="links">
            {!isMobile && <a className="link" href="/#features">Features</a>}
            {!isMobile && <a className="link" href="/#pricing">Pricing</a>}
            <a className="link" href="/privacy-policy">Privacy</a>
            <a className="link" href="/terms">Terms</a>
            <a className="link" onClick={handleLogin}>Login</a>
          </div>
        </nav>
      </div>
    </header>

    <section className="hero-section">
      <div className="container">
        <h1>Terms of Service</h1>
        <p className="lead">Last updated: April 15, 2026</p>
        <p className="description">These Terms govern your use of SerialMan. By using the platform, you agree to these terms.</p>
      </div>
    </section>

    <div className="container policy-container">
      <main className="policy-content">
        <section id="acceptance" className="policy-section">
          <h2>Acceptance of Terms</h2>
          <p>By accessing and using SerialMan for queue and appointment management, you agree to be bound by these Terms. If you do not agree, you must stop using the Service immediately.</p>
        </section>

        <section id="use-of-service" className="policy-section">
          <h2>Use of Service</h2>
          <p>SerialMan is provided for business and personal use only. You must use the Service in compliance with all applicable laws and must not:</p>
          <ul>
            <li>Engage in any illegal or fraudulent activity</li>
            <li>Attempt to hack, disrupt, or damage the Service</li>
            <li>Share your account credentials with others</li>
            <li>Remove, obscure, or alter any proprietary notices</li>
            <li>Scrape or automatically access the Service</li>
          </ul>
        </section>

        <section id="account-responsibility" className="policy-section">
          <h2>Account Responsibility</h2>
          <p>You are responsible for maintaining the confidentiality of your login credentials and all activity under your account. You must immediately notify us of any unauthorized access or suspicious activity.</p>
        </section>

        <section id="user-content" className="policy-section">
          <h2>User Content</h2>
          <p>You retain ownership of any data you upload to SerialMan. By using the Service, you grant HrnSoft permission to use your data to provide and improve the Service. You represent that all content you upload is original and does not violate anyone's rights.</p>
        </section>

        <section id="limitation-liability" className="policy-section">
          <h2>Limitation of Liability</h2>
          <p>To the maximum extent permitted by law, HrnSoft is not liable for indirect, incidental, special, or consequential damages. HrnSoft's total liability shall not exceed the amount you have paid us in the last 12 months, or USD $100, whichever is less.</p>
        </section>

        <section id="warranty-disclaimer" className="policy-section">
          <h2>Warranty Disclaimer</h2>
          <p>The Service is provided "as-is" and "as-available." We make no warranties, express or implied, regarding the Service. We do not guarantee the Service will be error-free, uninterrupted, or meet your specific requirements.</p>
        </section>

        <section id="termination" className="policy-section">
          <h2>Termination</h2>
          <p>You may cancel your account at any time through your account settings. We may suspend or terminate your account immediately if you violate these Terms, engage in illegal activity, or breach payment obligations.</p>
        </section>

        <section id="modifications" className="policy-section">
          <h2>Service Changes</h2>
          <p>We reserve the right to modify or discontinue the Service at any time. We will attempt to provide notice of significant changes, but we are not obligated to do so.</p>
        </section>

        <section id="payment" className="policy-section">
          <h2>Payment & Billing</h2>
          <p>Subscription fees are billed according to your chosen plan. Subscriptions automatically renew unless canceled. Refunds are available within 14 days for monthly plans and 30 days for annual plans. Setup fees and one-time charges are non-refundable.</p>
        </section>

        <section id="governing-law" className="policy-section">
          <h2>Governing Law</h2>
          <p>These Terms are governed by the laws of Bangladesh. Any disputes shall be subject to the exclusive jurisdiction of courts in Dhaka, Bangladesh.</p>
        </section>

        <section id="contact" className="policy-section">
          <h2>Contact</h2>
          <div className="contact-info">
            <p><strong>Email:</strong> legal@hrnsoft.com</p>
            <p><strong>Address:</strong> HrnSoft Inc., Dhaka, Bangladesh</p>
          </div>

          <p style={{ marginTop: "24px", paddingTop: "24px", borderTop: "1px solid rgba(39, 105, 135, 0.1)" }}>
            <strong>Last Updated:</strong> April 15, 2026
          </p>
        </section>
      </main>
    </div>

    <footer>
      <div className="container" style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "30px 0" }}>
        <div className="copyright">© 2026 HrnSoft. All rights reserved.</div>
      </div>
    </footer>

    {showScrollTop && (
      <button 
        className="scroll-to-top"
        onClick={handleScrollTop}
        title="Scroll to top"
        aria-label="Scroll to top"
      >
        ↑
      </button>
    )}
  </div>
  );
}
